import { useMemo } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { Check, ArrowRight, ArrowLeft, Phone } from "lucide-react";
import { BRAND } from "@/data/site";
import { useT } from "@/i18n/I18nProvider";
import { useSite } from "@/i18n/useSite";
import { PageIntro, Reveal } from "@/components/Section";
import { TalkCta } from "@/components/TalkCta";

const OtherServices = ({ slug }) => {
  const t = useT();
  const { SERVICES } = useSite();
  const others = useMemo(() => SERVICES.filter((o) => o.slug !== slug), [SERVICES, slug]);
  return (
    <div className="bg-[var(--blue-tint)] corner-bl p-7">
      <h4 className="text-sm font-semibold uppercase tracking-wide text-[var(--ink)]">{t("Other services")}</h4>
      <ul className="mt-4 divide-y divide-white">
        {others.map((o) => (
          <li key={o.slug}>
            <Link to={`/services/${o.slug}`} className="group flex items-center justify-between py-3 text-sm font-medium text-[var(--blue)] hover:text-[var(--coral)] transition-colors">
              <span className="flex items-center gap-3"><o.icon size={16} />{o.title}</span>
              <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
};

const EnquireCard = ({ title }) => {
  const t = useT();
  return (
    <div className="tile-blue corner-tr p-7">
      <h3 className="!text-white text-xl font-semibold">{t("Interested in {title}?").replace("{title}", title)}</h3>
      <p className="text-white/80 text-sm mt-2">{t("Make a referral or give us a call — we'll talk through how it could work for you.")}</p>
      <div className="mt-6 flex flex-col gap-2.5">
        <Link to="/referral-form" className="btn btn-coral" data-testid="service-refer-btn">{t("Make a referral")} <ArrowRight size={16} /></Link>
        <a href={`tel:${BRAND.phone.tel}`} className="btn btn-white" data-testid="service-phone-btn"><Phone size={16} /> {BRAND.phone.number}</a>
      </div>
    </div>
  );
};

export default function ServiceDetail() {
  const t = useT();
  const { SERVICES } = useSite();
  const { slug } = useParams();
  const s = SERVICES.find((x) => x.slug === slug);
  if (!s) return <Navigate to="/services" replace />;

  return (
    <>
      <PageIntro eyebrow={t("Our services")} title={s.title} text={s.intro} image={s.image}>
        <Link to="/services" className="mt-8 link-u inline-flex items-center gap-2 text-sm" data-testid="back-to-services"><ArrowLeft size={15} /> {t("All services")}</Link>
      </PageIntro>
      <section className="py-16 lg:py-24" data-testid="service-detail">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <Reveal>
              <span className="eyebrow">{t("What we can help with")}</span>
              <ul className="mt-5 space-y-3">
                {s.points.map((p, i) => (
                  <li key={i} className="flex items-start gap-3 p-4 bg-[var(--blue-tint)] corner-tr-sm">
                    <span className="w-6 h-6 mt-0.5 rounded-md bg-[var(--coral)] text-white flex items-center justify-center shrink-0"><Check size={13} strokeWidth={3} /></span>
                    <span className="text-[15px] text-[var(--ink)]">{p}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-8 text-base md:text-lg text-[var(--body)] leading-relaxed">{s.body}</p>
            </Reveal>
          </div>
          <aside className="lg:col-span-5">
            <Reveal delay={0.1} className="lg:sticky lg:top-32 space-y-5">
              <EnquireCard title={s.title} />
              <OtherServices slug={slug} />
            </Reveal>
          </aside>
        </div>
      </section>
      <TalkCta sourcePage={`service:${slug}`} />
    </>
  );
}
